import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEquipment } from '../../../context/EquipmentContext';
import { useAuth } from '../../../context/AuthContext';
import { useTeam } from '../../../context/TeamContext';
import { EQUIPMENT_TYPES } from '../../../data/nurseAppModels';
import { collection, query, where, getDocs, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';

const StockManagementPage = () => {
  const navigate = useNavigate();
  const { equipment, stockIn, approveStockIn, rejectStockIn } = useEquipment();
  const { currentUser } = useAuth();
  const { currentTeam } = useTeam();

  const [activeTab, setActiveTab] = useState('create'); // 'create', 'pending', 'history'
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rejectModal, setRejectModal] = useState({ open: false, transactionId: null, reason: '' });

  const [stockInForm, setStockInForm] = useState({
    equipmentId: '',
    quantity: '',
    source: 'PURCHASE',
    shiftRound: '',
    date: new Date(),
    notes: ''
  });

  // Fetch transactions
  const fetchTransactions = async () => {
    if (!currentTeam) return;

    try {
      setLoading(true);
      setError(null);
      
      // Try query with orderBy first (requires index)
      try {
        const transactionsQuery = query(
          collection(db, 'equipmentStockTransactions'),
          where('teamId', '==', currentTeam.id),
          orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(transactionsQuery);
        const transactionsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setTransactions(transactionsData);
        // Clear error if query succeeds
        setError(null);
      } catch (indexError) {
        // Check if it's an index error
        const isIndexError = indexError.code === 'failed-precondition' || 
                           indexError.code === 'unimplemented' ||
                           indexError.message?.includes('index') ||
                           indexError.message?.includes('requires an index');
        
        if (isIndexError) {
          console.warn('Index not found, using fallback query:', indexError);
          // Try fallback query without orderBy
          try {
            const fallbackQuery = query(
              collection(db, 'equipmentStockTransactions'),
              where('teamId', '==', currentTeam.id)
            );
            const snapshot = await getDocs(fallbackQuery);
            const transactionsData = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            // Sort in memory by createdAt
            transactionsData.sort((a, b) => {
              const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 
                           (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0);
              const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 
                           (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0);
              return bTime - aTime; // Descending
            });
            setTransactions(transactionsData);
            // Only show warning if fallback succeeds (index still missing)
            setError({
              type: 'index',
              message: '⚠️ ยังไม่มี Index ใน Firebase กรุณา deploy index หรือคลิกลิงก์ใน Console เพื่อสร้าง index',
              link: indexError.message?.match(/https:\/\/[^\s]+/)?.[0]
            });
          } catch (fallbackError) {
            // If fallback also fails, show error
            console.error('Fallback query also failed:', fallbackError);
            setError({
              type: 'error',
              message: `เกิดข้อผิดพลาดในการโหลดข้อมูล: ${fallbackError.message}`
            });
            setTransactions([]);
          }
        } else {
          // Other errors
          throw indexError;
        }
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError({
        type: 'error',
        message: `เกิดข้อผิดพลาดในการโหลดข้อมูล: ${err.message}`
      });
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [currentTeam]);

  const pendingTransactions = transactions.filter(tx => tx.status === 'PENDING');
  const historyTransactions = transactions.filter(tx => tx.status !== 'PENDING');

  const handleStockIn = async (e) => {
    e.preventDefault();
    if (!stockInForm.equipmentId || !stockInForm.quantity) {
      alert('กรุณากรอกข้อมูลให้ครบถ้วน');
      return;
    }

    try {
      const result = await stockIn(stockInForm.equipmentId, {
        quantity: parseInt(stockInForm.quantity),
        source: stockInForm.source,
        shiftRound: stockInForm.shiftRound || null,
        date: stockInForm.date,
        notes: stockInForm.notes
      });

      if (result.success) {
        alert('บันทึก Stock In สำเร็จ รอการอนุมัติ');
        setStockInForm({
          equipmentId: '',
          quantity: '',
          source: 'PURCHASE',
          shiftRound: '',
          date: new Date(),
          notes: ''
        });
        // Refresh transactions and switch to pending tab
        await fetchTransactions();
        setActiveTab('pending'); // Switch to pending tab to show the new request
      } else {
        alert(`เกิดข้อผิดพลาด: ${result.error}`);
      }
    } catch (err) {
      console.error('Error creating stock in:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    }
  };

  const handleApprove = async (transactionId) => {
    if (!window.confirm('ยืนยันการอนุมัติ Stock In?')) return;

    try {
      const result = await approveStockIn(transactionId);
      if (result.success) {
        alert('อนุมัติสำเร็จ');
        // Refresh transactions
        await fetchTransactions();
        // If no more pending, switch to history tab
        const updatedPending = transactions.filter(tx => tx.id !== transactionId && tx.status === 'PENDING');
        if (updatedPending.length === 0 && activeTab === 'pending') {
          setActiveTab('history');
        }
      } else {
        alert(`เกิดข้อผิดพลาด: ${result.error}`);
      }
    } catch (err) {
      console.error('Error approving stock in:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    }
  };

  const handleReject = async () => {
    if (!rejectModal.transactionId) return;
    if (!rejectModal.reason.trim()) {
      alert('กรุณาระบุเหตุผลในการไม่อนุมัติ');
      return;
    }

    try {
      const result = await rejectStockIn(rejectModal.transactionId, rejectModal.reason);
      if (result.success) {
        alert('ไม่อนุมัติสำเร็จ');
        // Refresh transactions
        await fetchTransactions();
        // Close modal
        setRejectModal({ open: false, transactionId: null, reason: '' });
        // If no more pending, switch to history tab
        const updatedPending = transactions.filter(tx => tx.id !== rejectModal.transactionId && tx.status === 'PENDING');
        if (updatedPending.length === 0 && activeTab === 'pending') {
          setActiveTab('history');
        }
      } else {
        alert(`เกิดข้อผิดพลาด: ${result.error}`);
      }
    } catch (err) {
      console.error('Error rejecting stock in:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    }
  };

  const openRejectModal = (transactionId) => {
    setRejectModal({ open: true, transactionId, reason: '' });
  };

  const closeRejectModal = () => {
    setRejectModal({ open: false, transactionId: null, reason: '' });
  };

  const smallItems = equipment.filter(eq => eq.type === EQUIPMENT_TYPES.SMALL_ITEM);

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/nurse/equipment')}
            className="mb-4 text-blue-600 hover:text-blue-700 flex items-center gap-2"
          >
            <span>←</span> กลับ
          </button>
          <h1 className="text-2xl font-bold text-gray-800">จัดการ Stock</h1>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('create')}
              className={`flex-1 px-4 py-3 text-center font-medium transition ${
                activeTab === 'create'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              📥 นำเข้า Stock
            </button>
            <button
              onClick={() => setActiveTab('pending')}
              className={`flex-1 px-4 py-3 text-center font-medium transition ${
                activeTab === 'pending'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              ⏳ รออนุมัติ ({pendingTransactions.length})
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`flex-1 px-4 py-3 text-center font-medium transition ${
                activeTab === 'history'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              📋 ประวัติ
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow-md p-6">
          {activeTab === 'create' && (
            <form onSubmit={handleStockIn} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  เลือกอุปกรณ์ *
                </label>
                <select
                  value={stockInForm.equipmentId}
                  onChange={(e) => setStockInForm({ ...stockInForm, equipmentId: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">-- เลือกอุปกรณ์ --</option>
                  {smallItems.map(eq => (
                    <option key={eq.id} value={eq.id}>
                      {eq.name} ({eq.code}) - Stock ปัจจุบัน: {eq.currentStock || 0} {eq.unit}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  จำนวน *
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    value={stockInForm.quantity}
                    onChange={(e) => setStockInForm({ ...stockInForm, quantity: e.target.value })}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <span className="text-gray-600">
                    {smallItems.find(eq => eq.id === stockInForm.equipmentId)?.unit || ''}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  แหล่งที่มา *
                </label>
                <select
                  value={stockInForm.source}
                  onChange={(e) => setStockInForm({ ...stockInForm, source: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="PURCHASE">สั่งซื้อของใหม่</option>
                  <option value="RETURN_FROM_REPAIR">กลับจากซ่อม</option>
                  <option value="RETURN_FROM_BORROW">นำกลับมาคืน</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  รอบเวร
                </label>
                <select
                  value={stockInForm.shiftRound}
                  onChange={(e) => setStockInForm({ ...stockInForm, shiftRound: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- เลือกรอบเวร --</option>
                  <option value="MORNING_BEFORE">เช้า - ก่อนเข้าเวร</option>
                  <option value="MORNING_AFTER">เช้า - หลังเข้าเวร</option>
                  <option value="AFTERNOON_BEFORE">บ่าย - ก่อนเข้าเวร</option>
                  <option value="AFTERNOON_AFTER">บ่าย - หลังเข้าเวร</option>
                  <option value="NIGHT_BEFORE">ดึก - ก่อนเข้าเวร</option>
                  <option value="NIGHT_AFTER">ดึก - หลังเข้าเวร</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  วันที่
                </label>
                <input
                  type="date"
                  value={stockInForm.date.toISOString().split('T')[0]}
                  onChange={(e) => setStockInForm({ ...stockInForm, date: new Date(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  หมายเหตุ
                </label>
                <textarea
                  value={stockInForm.notes}
                  onChange={(e) => setStockInForm({ ...stockInForm, notes: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="เพิ่มหมายเหตุ..."
                />
              </div>

              <button
                type="submit"
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
              >
                บันทึก Stock In
              </button>
            </form>
          )}

          {activeTab === 'pending' && (
            <div className="space-y-4">
              {error && error.type === 'index' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="text-sm text-yellow-800 mb-2">
                        {error.message}
                      </p>
                      {error.link && (
                        <a 
                          href={error.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-700 underline"
                        >
                          คลิกที่นี่เพื่อสร้าง Index
                        </a>
                      )}
                      <p className="text-xs text-yellow-600 mt-2">
                        หรือรันคำสั่ง: <code className="bg-yellow-100 px-2 py-1 rounded">firebase deploy --only firestore:indexes</code>
                      </p>
                      <p className="text-xs text-yellow-600 mt-1">
                        💡 ถ้าสร้าง index แล้ว ให้ refresh หน้านี้
                      </p>
                    </div>
                    <button
                      onClick={async () => {
                        setError(null);
                        await fetchTransactions();
                      }}
                      className="ml-4 px-3 py-1 bg-yellow-600 text-white rounded text-sm hover:bg-yellow-700"
                      title="ลอง query อีกครั้ง"
                    >
                      🔄 ลองใหม่
                    </button>
                  </div>
                </div>
              )}
              {error && error.type === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-red-800">{error.message}</p>
                  <button
                    onClick={fetchTransactions}
                    className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                  >
                    ลองใหม่
                  </button>
                </div>
              )}
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">กำลังโหลด...</p>
                </div>
              ) : pendingTransactions.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <div className="text-6xl mb-4">✅</div>
                  <p className="text-lg font-semibold mb-2">ไม่มีรายการรออนุมัติ</p>
                  <p className="text-sm mb-4">รายการที่อนุมัติแล้วจะแสดงในแท็บ "ประวัติ"</p>
                  <button
                    onClick={() => setActiveTab('create')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    สร้าง Stock In ใหม่
                  </button>
                </div>
              ) : (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-800">
                      💡 <strong>สำหรับ Leader:</strong> กรุณาตรวจสอบและอนุมัติรายการ Stock In ที่รอการอนุมัติ
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      เมื่ออนุมัติแล้ว Stock จะเพิ่มขึ้นทันที
                    </p>
                  </div>
                  {pendingTransactions.map(tx => {
                  const equipmentData = equipment.find(eq => eq.id === tx.equipmentId);
                  const date = tx.date?.toDate ? tx.date.toDate() : new Date(tx.date?.seconds * 1000);
                  
                  return (
                    <div key={tx.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-semibold text-gray-800">
                            {equipmentData?.name || 'ไม่พบอุปกรณ์'} ({equipmentData?.code || '-'})
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">
                            จำนวน: {tx.quantity} {equipmentData?.unit || ''}
                          </p>
                          <p className="text-sm text-gray-600">
                            แหล่งที่มา: {tx.source === 'PURCHASE' ? 'สั่งซื้อของใหม่' :
                                        tx.source === 'RETURN_FROM_REPAIR' ? 'กลับจากซ่อม' :
                                        'นำกลับมาคืน'}
                          </p>
                          <p className="text-sm text-gray-600">
                            โดย: {tx.performedByName} | {date.toLocaleDateString('th-TH')}
                          </p>
                          {tx.notes && (
                            <p className="text-sm text-gray-500 mt-2">หมายเหตุ: {tx.notes}</p>
                          )}
                        </div>
                        {(currentTeam?.userRole === 'LEADER' || currentTeam?.userRole === 'DEPUTY_LEADER') && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleApprove(tx.id)}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                            >
                              อนุมัติ
                            </button>
                            <button
                              onClick={() => openRejectModal(tx.id)}
                              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                            >
                              ไม่อนุมัติ
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                </>
              )}
            </div>
          )}

          {/* Reject Modal */}
          {rejectModal.open && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                <div className="p-6">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">ไม่อนุมัติ Stock In</h2>
                  <p className="text-sm text-gray-600 mb-4">
                    กรุณาระบุเหตุผลในการไม่อนุมัติ
                  </p>
                  <textarea
                    value={rejectModal.reason}
                    onChange={(e) => setRejectModal({ ...rejectModal, reason: e.target.value })}
                    placeholder="ระบุเหตุผล..."
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 mb-4"
                    required
                  />
                  <div className="flex gap-3">
                    <button
                      onClick={closeRejectModal}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      ยกเลิก
                    </button>
                    <button
                      onClick={handleReject}
                      disabled={!rejectModal.reason.trim()}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      ยืนยันไม่อนุมัติ
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-4">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">กำลังโหลด...</p>
                </div>
              ) : historyTransactions.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p>ไม่มีประวัติ</p>
                </div>
              ) : (
                historyTransactions.map(tx => {
                  const equipmentData = equipment.find(eq => eq.id === tx.equipmentId);
                  const date = tx.date?.toDate ? tx.date.toDate() : new Date(tx.date?.seconds * 1000);
                  const approvedDate = tx.approvedAt?.toDate ? tx.approvedAt.toDate() : null;
                  const rejectedDate = tx.rejectedAt?.toDate ? tx.rejectedAt.toDate() : null;
                  
                  return (
                    <div key={tx.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-gray-800">
                              {equipmentData?.name || 'ไม่พบอุปกรณ์'} ({equipmentData?.code || '-'})
                            </h3>
                            <span className={`px-2 py-1 text-xs rounded ${
                              tx.status === 'APPROVED' 
                                ? 'bg-green-100 text-green-700' 
                                : tx.status === 'REJECTED'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              {tx.status === 'APPROVED' 
                                ? 'อนุมัติแล้ว' 
                                : tx.status === 'REJECTED'
                                ? 'ไม่อนุมัติ'
                                : tx.status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">
                            จำนวน: {tx.quantity} {equipmentData?.unit || ''}
                          </p>
                          <p className="text-sm text-gray-600">
                            แหล่งที่มา: {tx.source === 'PURCHASE' ? 'สั่งซื้อของใหม่' :
                                        tx.source === 'RETURN_FROM_REPAIR' ? 'กลับจากซ่อม' :
                                        'นำกลับมาคืน'}
                          </p>
                          <p className="text-sm text-gray-600">
                            โดย: {tx.performedByName} | {date.toLocaleDateString('th-TH')}
                          </p>
                          {approvedDate && (
                            <p className="text-sm text-green-600">
                              ✅ อนุมัติโดย: {tx.approvedByName || '-'} | {approvedDate.toLocaleDateString('th-TH')}
                            </p>
                          )}
                          {rejectedDate && (
                            <p className="text-sm text-red-600">
                              ❌ ไม่อนุมัติโดย: {tx.rejectedByName || '-'} | {rejectedDate.toLocaleDateString('th-TH')}
                            </p>
                          )}
                          {tx.rejectionReason && (
                            <p className="text-sm text-red-500 mt-1">
                              เหตุผล: {tx.rejectionReason}
                            </p>
                          )}
                          {tx.notes && (
                            <p className="text-sm text-gray-500 mt-2">หมายเหตุ: {tx.notes}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StockManagementPage;

